/**
 * The operator vocabulary — every value read from the SCHEMA that already
 * exists, never invented here:
 *
 *   radaccess_operator_profile.operator_type       ISP | VNO | OPERATOR
 *   radaccess_operator_taxinfo.registration_status REGULAR | COMPOSITION | UNREGISTERED
 *   radaccess_operator_taxinfo.filing_frequency    MONTHLY | QUARTERLY
 *   radaccess_operator_license.license_type        UL | ISP_A | ISP_B | ISP_C | VNO
 *
 * 🚨 `radaccess_operator_license` EXISTS and radbilling READS it
 * (invoicenumber.go:48 selects invoice_series_code from it), but radaccess has
 * NO routes and NO handlers for it. Measured 2026-08-31: the table holds ZERO
 * rows. So nothing can populate it through any API today — the licence half of
 * this form has a schema and a reader and no writer.
 */
import { SELECTABLE_PROVIDER_KINDS, anyKindRequiresLicence } from "./labels";

export const OPERATOR_TYPES = ["ISP", "VNO", "OPERATOR"] as const;
export type OperatorType = (typeof OPERATOR_TYPES)[number];

export const REGISTRATION_STATUSES = ["REGULAR", "COMPOSITION", "UNREGISTERED"] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];

export const FILING_FREQUENCIES = ["MONTHLY", "QUARTERLY"] as const;
export type FilingFrequency = (typeof FILING_FREQUENCIES)[number];

export const LICENSE_TYPES = ["UL", "ISP_A", "ISP_B", "ISP_C", "VNO"] as const;
export type LicenseType = (typeof LICENSE_TYPES)[number];

/**
 * Tax entries are TYPED ROWS, matching radcustomer's TaxInfo
 * (taxtype/taxnum/taxdate/taximg/verified) rather than the operator table's
 * current two columns.
 *
 * 🔑 THE REASON IS MULTI-STATE. GST registration is per state: an operator
 * trading in two states holds two GSTINs, and `radaccess_operator_taxinfo`'s
 * single `gstin` column structurally cannot hold the second one. PAN is
 * national and there is exactly one. A shape that can only express one GSTIN
 * silently forces a wrong one onto every invoice raised from the other state.
 */
export const TAX_TYPES = ["PAN", "GSTIN"] as const;
export type TaxType = (typeof TAX_TYPES)[number];

export interface TaxEntry {
  taxtype: TaxType;
  taxnum: string;
  /** ISO date the registration/allotment took effect. Optional. */
  taxdate: string;
}

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/;

export function isValidPan(v: string): boolean {
  return PAN_RE.test(v.trim().toUpperCase());
}

/**
 * RBI format: four letters (bank), a reserved "0", then six branch characters.
 * 🔑 A format check is not an existence check -- a well-formed IFSC for a branch
 * that does not exist still fails at settlement, which is why the bank details
 * carry a `verified` flag that a reviewer sets, and why this only rejects the
 * shapes that CANNOT be right.
 */
export function isValidIfsc(v: string): boolean {
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v.trim().toUpperCase());
}

export function isValidGstin(v: string): boolean {
  return GSTIN_RE.test(v.trim().toUpperCase());
}

/**
 * 🔑 A GSTIN CONTAINS ITS OWN PAN — characters 3..12. That makes the two
 * fields checkable against each other rather than merely checkable
 * individually, and it catches the single most likely data-entry error on this
 * form: a GSTIN pasted from a different legal entity. Getting that wrong puts
 * the wrong registered person on a statutory document.
 */
export function panFromGstin(gstin: string): string {
  return gstin.trim().toUpperCase().slice(2, 12);
}

/**
 * Clearing the GSTIN when the status says there cannot be one.
 *
 * 🪤 DISABLING AN INPUT DOES NOT CLEAR IT. A greyed-out field keeps its value
 * and still submits it, so an applicant who types a GSTIN and then switches to
 * UNREGISTERED sends a number they can no longer see or edit. radaccess's
 * validateTaxInfo refuses that exact pair -- "an UNREGISTERED operator must not
 * carry a gstin" -- so the form would report an error about a field it is
 * hiding, and the applicant has no way to act on it.
 *
 * Kept here rather than inside the form because BOTH the application form and
 * the profile screen let the status change, and a rule enforced in one of them
 * is enforced nowhere.
 */
export function clearGstinIfUnregistered(
  tax: TaxEntry[],
  status: RegistrationStatus,
): TaxEntry[] {
  if (status !== "UNREGISTERED") return tax;
  return tax.map((e) => (e.taxtype === "GSTIN" ? { ...e, taxnum: "", taxdate: "" } : e));
}

export interface OperatorApplication {
  legalName: string;
  operatorType: OperatorType;
  /**
   * What this provider sells -- keys from PROVIDER_KINDS. A LIST, not one value.
   *
   * 🔑 ONE PROVIDER USUALLY DOES SEVERAL OF THESE. Most ISPs also sell hosting,
   * cloud and OTT alongside access; modelling this as a single choice would
   * force them to under-declare, and the licence question would then be decided
   * by whichever one they happened to pick.
   *
   * 🔑 NOT `operatorType`. That is the LICENCE class (ISP | VNO | OPERATOR) and
   * only means anything to a licensed provider. This says what the business
   * sells, and it decides whether a licence is asked for at all.
   */
  providerKinds: string[];
  /**
   * The applicant has read the namespace guide.
   *
   * 🔑 A GATE, NOT A DISCLAIMER. The namespace field stays disabled until this
   * is ticked, because the namespace is permanent and every containment check
   * on the platform trusts it — and applicants routinely choose one before
   * learning it is a tree. Hand-reviewing those names is the single manual step
   * blocking automatic onboarding, so the cheapest fix is to make the concept
   * unavoidable at the moment of choosing rather than correcting it afterwards.
   */
  namespaceGuideRead: boolean;
  /** Namespace the applicant is asking for. Assigned/confirmed on approval. */
  requestedNamespace: string;
  /** The upstream provider's namespace, when this operator sells under one. */
  upstreamNamespace: string;
  registrationStatus: RegistrationStatus;
  filingFrequency: FilingFrequency;
  tax: TaxEntry[];
  holdsLicense: boolean;
  licenseType: LicenseType | "";
  licenseNumber: string;
  /** The entity named ON the licence — need not equal legalName. */
  licensedEntity: string;
  licenseValidFrom: string;
  licenseValidTo: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  contactName: string;
  contactEmail: string;
  contactMobile: string;
  /**
   * Settlement bank account.
   *
   * 🔑 Required because money moves OUT to the provider. Subscribers pay through
   * the payment gateway, and settlement needs an account to land in -- an
   * approved provider with no bank details cannot be paid, so the gap would only
   * surface at the first settlement run.
   *
   * ⚠️ radaccess_operator_bankinfo.verified defaults to 0 and the owner ruled
   * (2026-09-05) that bank verification is MANDATORY before approval. Collecting
   * the details is not verifying them; the reviewer still has to.
   */
  bankAccountHolder: string;
  bankAccountNumber: string;
  bankIfsc: string;
  bankName: string;
}

export const EMPTY_APPLICATION: OperatorApplication = {
  legalName: "",
  operatorType: "OPERATOR",
  // Empty, not pre-ticked. An applicant silently defaulted into a licensed kind
  // would be asked for paperwork that does not exist for their business.
  providerKinds: [],
  namespaceGuideRead: false,
  requestedNamespace: "",
  upstreamNamespace: "",
  registrationStatus: "REGULAR",
  filingFrequency: "MONTHLY",
  tax: [
    { taxtype: "PAN", taxnum: "", taxdate: "" },
    { taxtype: "GSTIN", taxnum: "", taxdate: "" },
  ],
  holdsLicense: false,
  licenseType: "",
  licenseNumber: "",
  licensedEntity: "",
  licenseValidFrom: "",
  licenseValidTo: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  contactName: "",
  contactEmail: "",
  contactMobile: "",
  bankAccountHolder: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankName: "",
};

export function taxNum(app: OperatorApplication, t: TaxType): string {
  return (app.tax.find((e) => e.taxtype === t)?.taxnum ?? "").trim().toUpperCase();
}

/**
 * Namespaces are dot-delimited and hierarchical, and a namespace IS the
 * operator's identity everywhere on this platform. The same character set the
 * backends' sanitizer accepts, so a name that passes here cannot be rejected
 * later by the gate.
 */
const NAMESPACE_RE = /^[a-z0-9]+(\.[a-z0-9-]+)+$/;

/**
 * 🔑 NORMALIZE, DON'T REFUSE, ON CASE. The backends' `sanitize.Domain`
 * lowercases the namespace before the containment check, so "Net.X" and
 * "net.x" are already the same tenant to every gate on the platform. Refusing
 * the capital here would reject a name the system would then happily accept
 * from another entry point — two rules for one identifier. Normalize on the
 * way in and say so, so what the operator typed and what they get are the same
 * thing.
 */
export function normalizeNamespace(v: string): string {
  return v.trim().toLowerCase();
}

export function validateApplication(app: OperatorApplication): string[] {
  const p: string[] = [];

  if (!app.legalName.trim()) p.push("Legal name is required.");
  if (!app.contactName.trim()) p.push("A contact name is required.");
  if (!app.contactEmail.trim()) p.push("A contact email is required.");
  if (!app.contactMobile.trim()) p.push("A contact mobile is required.");
  if (!app.addressLine1.trim()) p.push("An address is required.");
  if (!app.city.trim()) p.push("A city is required.");
  if (!app.state.trim()) p.push("A state is required — it determines place of supply.");
  if (!app.pincode.trim()) p.push("A pincode is required.");

  // Settlement account. Required for every kind of provider: the money path is
  // the same whether the subscriber bought broadband or a SaaS seat.
  if (!app.bankAccountHolder.trim()) p.push("The bank account holder's name is required.");
  if (!app.bankAccountNumber.trim()) p.push("A bank account number is required.");
  else if (!/^[0-9]{6,18}$/.test(app.bankAccountNumber.trim()))
    p.push("A bank account number is 6 to 18 digits, with no spaces or punctuation.");
  if (!app.bankName.trim()) p.push("The bank's name is required.");
  const ifsc = app.bankIfsc.trim().toUpperCase();
  if (!ifsc) p.push("An IFSC is required — it identifies the branch settlement is sent to.");
  else if (!isValidIfsc(ifsc))
    p.push("IFSC must be four letters, a zero, then six letters or digits — e.g. HDFC0001234.");

  if (app.providerKinds.length === 0)
    p.push("Choose at least one service you provide — it decides which details are needed.");
  const unknown = app.providerKinds.filter(
    (k) => !SELECTABLE_PROVIDER_KINDS.some((s) => s.key === k),
  );
  if (unknown.length)
    p.push(`Not a service this form recognises: ${unknown.join(", ")}.`);

  if (!app.namespaceGuideRead)
    p.push("Read the namespace guide and confirm it before choosing a namespace.");

  const ns = normalizeNamespace(app.requestedNamespace);
  if (!ns) p.push("A requested namespace is required.");
  else if (!NAMESPACE_RE.test(ns))
    p.push(
      "The namespace must be dot-delimited with at least two parts, e.g. net.example.pune. " +
        "Letters, digits and hyphens only; it is stored lowercase.",
    );

  // PAN is REQUIRED. GSTIN is OPTIONAL, because COMPOSITION and UNREGISTERED
  // operators are a normal configuration, not an edge case.
  const pan = taxNum(app, "PAN");
  const gstin = taxNum(app, "GSTIN");

  if (!pan) p.push("PAN is required.");
  else if (!isValidPan(pan)) p.push("PAN must be five letters, four digits and a letter.");

  if (gstin && !isValidGstin(gstin)) p.push("GSTIN must be 15 characters in the standard format.");

  // The cross-check. Only meaningful when both are individually well-formed.
  if (pan && gstin && isValidPan(pan) && isValidGstin(gstin) && panFromGstin(gstin) !== pan) {
    p.push(
      `The GSTIN embeds a different PAN (${panFromGstin(gstin)}) than the one given (${pan}) — ` +
        "they must belong to the same legal entity.",
    );
  }

  if (app.registrationStatus === "REGULAR" && !gstin)
    p.push("A REGULAR registration requires a GSTIN. Choose COMPOSITION or UNREGISTERED instead.");
  if (app.registrationStatus === "UNREGISTERED" && gstin)
    p.push("An UNREGISTERED provider must not supply a GSTIN.");

  if (app.holdsLicense) {
    if (!app.licenseType) p.push("A licence type is required when the provider holds a licence.");
    if (!app.licenseNumber.trim()) p.push("A licence number is required.");
    if (!app.licensedEntity.trim())
      p.push("The entity named on the licence is required — it need not equal the legal name.");
    if (!app.licenseValidFrom) p.push("A licence valid-from date is required.");
    if (app.licenseValidFrom && app.licenseValidTo) {
      if (new Date(app.licenseValidTo).getTime() <= new Date(app.licenseValidFrom).getTime())
        p.push("The licence valid-to date must be after valid-from.");
    }
  }

  // 🚨 THIS RULE APPLIES ONLY TO LICENSED KINDS, and that is the whole fix.
  //
  // A provider that does not hold a licence cannot invoice pure internet service
  // in its own name, so it must name the upstream that does -- the two-entity
  // split the GST rules turn on, captured while someone is looking at the
  // paperwork. That reasoning is entirely about INTERNET service.
  //
  // Applied to every applicant it demanded a telecom licence, or an upstream
  // telecom licence holder, from cloud, hosting, OTT and SaaS providers. None of
  // them has either, because neither exists for their business -- so the form
  // rejected, with a message about internet invoicing, exactly the providers the
  // public site invites. Owner ruling 2026-09-07: only internet and VPN
  // providers are licensed.
  if (anyKindRequiresLicence(app.providerKinds) && !app.holdsLicense && !app.upstreamNamespace.trim())
    p.push(
      "An operator without its own licence must name an upstream provider — internet service is " +
        "invoiced in the licence holder's name.",
    );
  if (app.upstreamNamespace.trim() && normalizeNamespace(app.upstreamNamespace) === ns)
    p.push("The upstream cannot be this provider itself.");

  return p;
}
